import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../utils/IconHandler';
import { formatDateTime } from '../../utils/DateUtils';
import { getStockHistory } from '../../services/StockEventService';

const EVENT_TYPE_LABELS = {
  SALE: 'stock_event_sale',
  SALE_CANCELLATION: 'stock_event_sale_cancellation',
  RETURN: 'stock_event_return',
  B2B_ORDER: 'stock_event_b2b_order',
  B2B_ORDER_CANCELLATION: 'stock_event_b2b_order_cancellation',
  SITES_ORDER: 'stock_event_sites_order',
  SITES_ORDER_CANCELLATION: 'stock_event_sites_order_cancellation',
  REPLENISHMENT: 'stock_event_replenishment',
  TRANSFER_OUT: 'stock_event_transfer_out',
  TRANSFER_IN: 'stock_event_transfer_in',
  STOCK_COUNT_ADJUSTMENT: 'stock_event_stock_count_adjustment',
  STOCK_COUNT_VERIFIED: 'stock_event_stock_count_verified',
  MANUAL_EDIT: 'stock_event_manual_edit',
  PRODUCTION_CONSUME: 'stock_event_production_consume',
  PRODUCTION_OUTPUT: 'stock_event_production_output',
  BULK_IMPORT: 'stock_event_bulk_import'
};

const EVENT_TYPE_FALLBACK_ES = {
  SALE: 'Venta',
  SALE_CANCELLATION: 'Cancelación de venta',
  RETURN: 'Devolución',
  B2B_ORDER: 'Orden B2B',
  B2B_ORDER_CANCELLATION: 'Cancelación de orden B2B',
  SITES_ORDER: 'Orden ecommerce',
  SITES_ORDER_CANCELLATION: 'Cancelación de orden ecommerce',
  REPLENISHMENT: 'Reposición',
  TRANSFER_OUT: 'Traspaso (salida)',
  TRANSFER_IN: 'Traspaso (entrada)',
  STOCK_COUNT_ADJUSTMENT: 'Ajuste de conteo',
  STOCK_COUNT_VERIFIED: 'Conteo verificado',
  MANUAL_EDIT: 'Edición manual',
  PRODUCTION_CONSUME: 'Consumo de producción',
  PRODUCTION_OUTPUT: 'Producción',
  BULK_IMPORT: 'Importación masiva'
};

export const StockHistoryContent = ({ active = true, locationId, referenceType, referenceId }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!active || !locationId || !referenceType || !referenceId) return;
    setLoading(true);
    try {
      const data = await getStockHistory(locationId, referenceType, referenceId, page + 1, pageSize);
      setEvents(data.events || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching stock history:', error);
      setEvents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [active, locationId, referenceType, referenceId, page, pageSize]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (active) setPage(0);
  }, [active, referenceId, referenceType, locationId]);

  const actorLabel = (event) => {
    if (event.userEmail) return event.userEmail;
    if (event.actorType === 'CUSTOMER') return t('stock_event_actor_customer') || 'Cliente';
    if (event.actorType === 'SYSTEM') return t('stock_event_actor_system') || 'Sistema';
    return '-';
  };

  const eventTypeLabel = (eventType) => t(EVENT_TYPE_LABELS[eventType]) || EVENT_TYPE_FALLBACK_ES[eventType] || eventType;

  return (
    <>
      {loading && events.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('stock_history_empty') || 'Todavía no hay movimientos registrados para este ítem.'}
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('date') || 'Fecha'}</TableCell>
                <TableCell>{t('stock_event_type') || 'Tipo'}</TableCell>
                <TableCell align="center">{t('quantity') || 'Cantidad'}</TableCell>
                <TableCell align="center">{t('stock_after') || 'Stock resultante'}</TableCell>
                <TableCell>{t('user') || 'Usuario'}</TableCell>
                <TableCell>{t('note') || 'Nota'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(event.createdAt)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={eventTypeLabel(event.eventType)} />
                  </TableCell>
                  <TableCell align="center" sx={{ color: event.quantityDelta >= 0 ? 'success.main' : 'error.main', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {event.quantityDelta > 0 ? '+' : ''}{parseFloat(event.quantityDelta).toFixed(2)}
                  </TableCell>
                  <TableCell align="center">{parseFloat(event.stockAfter).toFixed(2)}</TableCell>
                  <TableCell>{actorLabel(event)}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>{event.note || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        )}
    </>
  );
};

const StockHistoryModal = ({ open, onClose, locationId, referenceType, referenceId, itemName }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          {t('stock_history') || 'Historial de stock'}
          {itemName && <Typography variant="body2" color="text.secondary">{itemName}</Typography>}
        </Box>
        <IconButton size="small" onClick={onClose}>{getIcon('close')}</IconButton>
      </DialogTitle>
      <DialogContent>
        <StockHistoryContent
          active={open}
          locationId={locationId}
          referenceType={referenceType}
          referenceId={referenceId}
        />
      </DialogContent>
    </Dialog>
  );
};

export default StockHistoryModal;
